import React from 'react';
import { ifStringEmpty, randomKey } from '../../../static/utils';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import * as ROLES from '../../../constants/roles';

import DatePicker from '../datePicker/DatePicker';
import { Container, Row, Col } from 'reactstrap';
import './Form.css';
import industries from '../../../static/industries';

import {
    doCreateUserWithEmailAndPassword,
    doSignInWithEmailAndPassword,
} from '../../../firebase/api/auth';
import {
    createUser,
    createUserAfterInviteToProject,
    getUserByEmail,
    getUserByInviteCode,
    updateUser,
} from '../../../firebase/api/users';

import { convertDateToFirestoreTimestamp } from '../../../firebase/api/firestoreUtils';
import * as ROUTES from '../../../constants/routes';
import { createStripeCustomer } from '../../../firebase/api/stripe';
import WithRouter from '../../../helpers/WithRouter';
import { sendGridEmailAfterSignUp } from '../../../firebase/api/mail';
import { getConfirmEmail } from '../../../templates';

const INITIAL_STATE = {
    dto: {
        uid: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        position: '',
        websiteLink: '',
        role: ROLES.USER,
        registrationTimestamp: null,
        isPremium: true,
        getPremiumTimestamp: null,
        inviteCode: 'random generated value',
        invitedBy: null,
        acceptPolicy: false,
        birthdayTimestamp: null,
        contactInfo: '',
        friends: [],
    },
    error: null,
};

class SignUpForm extends React.Component {
    state = { ...INITIAL_STATE };

    onSubmit = async event => {
        event.preventDefault();

        const {
            dto: { password, ...dto },
        } = this.state;

        try {
            const getUserDataFromCollection = await getUserByEmail(dto.email);

            if (getUserDataFromCollection !== null) {
                // if user have been already invited to the some project and his data object already exist in users collection
                if (getUserDataFromCollection.firstName) {
                    throw new Error('The email address is already in use by another account.');
                }
                const dataForCreateUser = {
                    uid: getUserDataFromCollection.uid,
                    email: getUserDataFromCollection.email,
                    password: password,
                };

                await createUserAfterInviteToProject(dataForCreateUser);
                await doSignInWithEmailAndPassword(dto.email, password);

                this.setDefaultFieldsBeforeCreate(getUserDataFromCollection.uid, dto);
                if (this.props.invitationKey) {
                    const [invitedBy] = await getUserByInviteCode(this.props.invitationKey);
                    dto.invitedBy = invitedBy.data().uid;
                }

                await updateUser(dto).then(() => {
                    const recipientInfo = {
                        email: dto.email,
                        uid: dto.uid,
                        firstName: dto.firstName,
                        lastName: dto.lastName,
                    };
                    sendGridEmailAfterSignUp(getConfirmEmail(recipientInfo));
                });
            } else {
                // if user not exist in users collection
                const { user } = await doCreateUserWithEmailAndPassword(dto.email, password);
                this.setDefaultFieldsBeforeCreate(user.uid, dto);
                if (this.props.invitationKey) {
                    const [invitedBy] = await getUserByInviteCode(this.props.invitationKey);
                    dto.invitedBy = invitedBy.data().uid;
                }

                await createUser(dto.uid, dto).then(newUser => {
                    const recipientInfo = {
                        email: newUser.email,
                        uid: newUser.uid,
                        firstName: newUser.firstName,
                        lastName: newUser.lastName,
                    };
                    sendGridEmailAfterSignUp(getConfirmEmail(recipientInfo));
                });
            }

            if (this.props.toggle) {
                this.props.toggle();
            }
            this.props.navigate(ROUTES.POLICY, {
                previewPostUid: this.props.previewPostUid,
            });
        } catch (error) {
            this.setState({ error });
        }
    };

    setDefaultFieldsBeforeCreate = (uid, dto) => {
        dto.uid = uid;
        dto.registrationTimestamp = convertDateToFirestoreTimestamp(new Date());
        dto.inviteCode = this.props.inviteCode ?? INITIAL_STATE.dto.inviteCode;
        dto.myPostsLastCheckTimestamp = convertDateToFirestoreTimestamp(new Date());
    };

    onChange = event => {
        const dto = { ...this.state.dto };
        dto[event.target.name] = event.target.value;
        this.setState({ dto });
    };

    onBirthdayDateChange = value => {
        const dto = { ...this.state.dto };
        dto.birthdayTimestamp = convertDateToFirestoreTimestamp(new Date(value));
        this.setState({ dto });
    };

    render() {
        const { dto, error } = this.state;
        const isInvalid =
            ifStringEmpty(dto.password) || ifStringEmpty(dto.email) || ifStringEmpty(dto.firstName);

        return (
            <Container fluid className="Form">
                <form
                    name="sign-up-form"
                    action=""
                    method="post"
                    className="signup-form"
                    onSubmit={this.onSubmit}
                >
                    <div className="form-group">
                        <label htmlFor="exampleInputEmail1">First name</label>
                        <input
                            type="text"
                            name="firstName"
                            className="form-control"
                            id="firstName"
                            placeholder="*Enter your first name"
                            onChange={this.onChange}
                        />
                    </div>

                    <div className="form-group">
                        <input
                            type="text"
                            name="lastName"
                            className="form-control"
                            id="lastName"
                            placeholder="Enter your second name"
                            onChange={this.onChange}
                        />
                    </div>

                    <div className="form-group">
                        <label>Professional Title</label>
                        <input
                            type="text"
                            name="position"
                            className="form-control"
                            id="position"
                            placeholder="*Job title"
                            onChange={this.onChange}
                        />
                    </div>

                    <div className="form-group">
                        <label>Personal/company website (optional)</label>
                        <input
                            type="text"
                            name="website"
                            className="form-control"
                            id="website"
                            placeholder="Enter website/ portfolio URL"
                            onChange={this.onChange}
                        />
                    </div>

                    <div className="form-group">
                        <label>Email address</label>
                        <input
                            type="email"
                            name="email"
                            className="form-control"
                            id="email"
                            aria-describedby="emailHelp"
                            placeholder="*Enter email"
                            onChange={this.onChange}
                        />
                    </div>

                    <div className="form-group">
                        <label>Password</label>
                        <input
                            type="password"
                            name="password"
                            className="form-control"
                            id="password"
                            placeholder="*Enter password"
                            onChange={this.onChange}
                        />
                    </div>
                    <div className="form-group">
                        <div className="down-arrow-dropdown"></div>
                        <label>Industry</label>
                        <select
                            name="industry"
                            className="form-control"
                            id="industry"
                            placeholder="Your industry"
                            onChange={this.onChange}
                        >
                            <option value="" disabled selected>
                                *Select your industry
                            </option>
                            {industries.map(industry => (
                                <option key={industry.id} value={industry.text}>
                                    {industry.text}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <div className="d-flex justify-content-end">
                            <button
                                type="submit"
                                className="btn btn-primary btn-sign-up"
                                disabled={isInvalid}
                            >
                                Sign Up
                            </button>
                        </div>
                    </div>

                    {error && <p>{error.message}</p>}
                </form>
            </Container>
        );
    }
}

export default WithRouter(SignUpForm);
